import { useParams, Link } from 'react-router-dom';
import { useDocumentMeta } from '@/hooks/useDocumentMeta';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/base/ScrollToTop';
import Breadcrumb from '@/components/base/Breadcrumb';
import { games, categories } from '@/mocks/games';

export default function CategoryDetail() {
  const { categoryId } = useParams<{ categoryId: string }>();
  const category = categories.find((c) => c.id === categoryId);
  const categoryGames = games.filter((g) => g.category === categoryId);

  useDocumentMeta(
    `${category?.name || '分类'} - 九游娱乐游戏大厅`,
    `九游娱乐${category?.name || '游戏'}专区，精选多款精品${category?.name || '游戏'}，安全流畅的游戏体验，丰厚奖励等你来拿。`
  );

  if (!category) {
    return (
      <>
        <ScrollToTop />
        <div className="min-h-screen bg-background-50">
          <Navbar />
          <main className="pt-24 pb-20">
            <div className="w-full px-4 md:px-6 max-w-7xl mx-auto text-center">
              <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-2xl bg-background-100">
                <i className="ri-error-warning-line text-3xl text-foreground-400"></i>
              </div>
              <h1 className="text-xl font-bold text-foreground-900 mb-2">分类不存在</h1>
              <Link to="/categories" className="text-primary-600 hover:text-primary-700 text-sm">
                返回游戏大厅
              </Link>
            </div>
          </main>
          <Footer />
        </div>
      </>
    );
  }

  const otherCategories = categories.filter((c) => c.id !== 'all' && c.id !== categoryId);

  return (
    <>
      <ScrollToTop />
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main>
          <section className="pt-24 pb-10 bg-background-100">
            <div className="w-full px-4 md:px-6 max-w-7xl mx-auto">
              <Breadcrumb items={[
                { label: '游戏大厅', href: '/categories' },
                { label: category.name },
              ]} />
              <div className="mt-6">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-primary-500">
                    <i className={`${category.icon} text-lg text-background-50`}></i>
                  </div>
                  <div>
                    <h1 className="text-2xl md:text-3xl font-bold font-heading text-foreground-950">
                      {category.name}
                    </h1>
                    <p className="text-sm text-foreground-500">共 {categoryGames.length} 款游戏</p>
                  </div>
                </div>
              </div>
            </div>
          </section>

          <section className="w-full py-10 bg-background-50">
            <div className="w-full px-4 md:px-6 max-w-7xl mx-auto">
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
                {categoryGames.map((game) => (
                  <div
                    key={game.id}
                    className="group bg-background-50 border border-background-200/70 rounded-xl overflow-hidden hover:border-primary-300 transition-all duration-300"
                  >
                    <div className="relative aspect-[4/3] overflow-hidden">
                      <img
                        src={game.image}
                        alt={game.name}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                      <div className="absolute top-3 left-3 flex flex-wrap gap-1.5">
                        {game.isHot && (
                          <span className="px-2 py-0.5 rounded-md bg-primary-500 text-background-50 text-xs font-semibold">热门</span>
                        )}
                        {game.isNew && (
                          <span className="px-2 py-0.5 rounded-md bg-accent-500 text-foreground-950 text-xs font-semibold">新游</span>
                        )}
                      </div>
                    </div>
                    <div className="p-4">
                      <div className="flex items-center justify-between mb-2">
                        <h3 className="text-base font-semibold text-foreground-900">{game.name}</h3>
                        <span className="flex items-center gap-1 text-xs text-accent-600">
                          <i className="ri-star-fill text-xs"></i>
                          {game.rating}
                        </span>
                      </div>
                      <p className="text-sm text-foreground-600 line-clamp-2 leading-relaxed mb-3">
                        {game.description}
                      </p>
                      <div className="flex items-center justify-between">
                        <span className="text-xs text-foreground-500">
                          <i className="ri-user-line mr-1"></i>
                          {game.players}玩家
                        </span>
                        <div className="flex flex-wrap gap-1">
                          {game.tags.map((tag) => (
                            <span key={tag} className="px-1.5 py-0.5 rounded bg-secondary-100 text-secondary-700 text-xs">
                              {tag}
                            </span>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              <div className="mt-12">
                <h2 className="text-lg font-bold text-foreground-900 mb-6">探索其他分类</h2>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                  {otherCategories.slice(0, 4).map((cat) => (
                    <Link
                      key={cat.id}
                      to={`/category/${cat.id}`}
                      className="flex items-center gap-3 p-4 rounded-xl bg-background-100 hover:bg-background-200 border border-background-200/70 transition-all duration-200"
                    >
                      <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-100/70">
                        <i className={`${cat.icon} text-lg text-primary-600`}></i>
                      </div>
                      <span className="text-sm font-medium text-foreground-800 whitespace-nowrap">{cat.name}</span>
                    </Link>
                  ))}
                </div>
              </div>
            </div>
          </section>
        </main>
        <Footer />
      </div>
    </>
  );
}